import { useEffect, useRef } from 'react';

// Real user-activity detection (not just tab-visibility) — genuinely idle means no mouse/
// keyboard/touch input for the threshold, not just "tab is in the background." Combined with
// visibilitychange so a hidden tab counts as idle immediately, same signal CustomerPortal.tsx's
// existing per-job hub already uses, just generalized here for reuse.
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'pointerdown'] as const;

const DEFAULT_IDLE_MS = 15 * 60 * 1000; // 15 minutes

interface UseIdleDisconnectOptions {
    /** Called once when the user goes idle (or the tab is hidden) — stop hubs / pause polling here. */
    onIdle: () => void;
    /** Called once when activity resumes (or the tab becomes visible again) — reconnect / resume here. */
    onActive: () => void;
    /** Milliseconds of no activity before onIdle fires. Default 15 minutes. */
    idleMs?: number;
    /**
     * When true, never goes idle regardless of activity or visibility — e.g. a contractor with an
     * active EnRoute/InProgress job shouldn't lose their dispatch channel just because their hands
     * are on the wheel, not the screen. If already idle when this flips true, forces an immediate
     * reconnect (onActive) rather than waiting for the next real input event.
     */
    suppressIdle?: boolean;
}

export function useIdleDisconnect({ onIdle, onActive, idleMs = DEFAULT_IDLE_MS, suppressIdle = false }: UseIdleDisconnectOptions) {
    const idleRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Refs so the effect's listener closures always call the latest callbacks without needing
    // onIdle/onActive themselves in the dependency array (callers pass inline functions).
    const onIdleRef = useRef(onIdle);
    const onActiveRef = useRef(onActive);
    onIdleRef.current = onIdle;
    onActiveRef.current = onActive;

    useEffect(() => {
        const goIdle = () => {
            if (idleRef.current || suppressIdle) return;
            idleRef.current = true;
            // Going idle via the visibilitychange path (tab hidden) fires this before the
            // inactivity timer would have — clear it so it doesn't fire again later as a
            // harmless-but-orphaned no-op.
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
            onIdleRef.current();
        };
        const goActive = () => {
            if (!idleRef.current) return;
            idleRef.current = false;
            onActiveRef.current();
        };
        const resetTimer = () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            if (document.hidden || suppressIdle) return; // visibilitychange/suppression handle those cases directly
            timerRef.current = setTimeout(goIdle, idleMs);
        };

        // suppressIdle just turned on while we were already idle (e.g. a new job was just
        // assigned mid-idle) — reconnect immediately rather than waiting for a real input event.
        if (suppressIdle && idleRef.current) goActive();

        const onActivity = () => { goActive(); resetTimer(); };
        const onVisibility = () => {
            if (document.hidden) goIdle();
            else { goActive(); resetTimer(); }
        };

        ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, onActivity, { passive: true }));
        document.addEventListener('visibilitychange', onVisibility);
        resetTimer();

        return () => {
            ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, onActivity));
            document.removeEventListener('visibilitychange', onVisibility);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [idleMs, suppressIdle]);
}
